<template>
	<div class="min-h-screen bg-gray-50">
		<div class="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center flex-wrap gap-4">
			<div class="flex flex-col gap-2">
				<router-link to="/themecategory" class="flex items-center gap-2 text-gray-500 no-underline text-sm hover:text-orange-7">
					<i class="fa fa-arrow-left"></i>
					<span>Back to Categories</span>
				</router-link>
				<h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Theme Category</h1>
			</div>
			<button
				v-if="!isLoading"
				class="bg-orange-7 text-white border-none rounded-md px-5 py-2.5 text-sm font-medium cursor-pointer hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
				:disabled="isSaving"
				@click="saveCategory"
			>
				{{ isSaving ? 'Saving...' : 'Save Changes' }}
			</button>
		</div>

		<div class="p-6">
			<div v-if="isLoading" class="py-12 px-6 text-center text-gray-500">
				Loading...
			</div>

			<div v-else>
				<!-- Tabs -->
				<div class="flex border-b border-gray-200 mb-6 gap-0">
					<button
						:class="[
							'bg-transparent border-none border-b-2 px-6 py-3 text-sm font-medium cursor-pointer',
							activeTab === 'properties' ? 'text-orange-7 border-orange-7' : 'text-gray-500 border-transparent hover:text-gray-700'
						]"
						@click="activeTab = 'properties'"
					>
						Properties
					</button>
					<button
						:class="[
							'bg-transparent border-none border-b-2 px-6 py-3 text-sm font-medium cursor-pointer',
							activeTab === 'themes' ? 'text-orange-7 border-orange-7' : 'text-gray-500 border-transparent hover:text-gray-700'
						]"
						@click="activeTab = 'themes'"
					>
						Linked Themes
					</button>
				</div>

				<!-- Properties Tab -->
				<div v-show="activeTab === 'properties'" class="mb-6">
					<div class="bg-white rounded-lg shadow-sm overflow-hidden">
						<div class="flex justify-between items-center px-6 py-4 border-b border-gray-200">
							<h2 class="text-lg font-semibold text-gray-900 m-0">Category Properties</h2>
						</div>
						<div class="p-6">
							<div class="mb-4">
								<label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
								<input
									v-model="category.name"
									type="text"
									class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
								>
							</div>

							<div class="mb-4">
								<label class="block text-sm font-medium text-gray-700 mb-1">Locale</label>
								<input
									v-model="category.locale"
									type="text"
									placeholder="e.g., en, de, fr"
									class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
								>
							</div>

							<div class="mb-4">
								<label class="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
								<input
									v-model.number="category.serialnumber"
									type="number"
									class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
								>
							</div>

							<div class="mb-4">
								<label class="block text-sm font-medium text-gray-700 mb-1">Tracker</label>
								<select v-model="category.trackerid" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
									<option :value="undefined">-- Select Tracker --</option>
									<option
										v-for="t in trackers"
										:key="t.id"
										:value="t.id"
									>
										{{ t.name }}
									</option>
								</select>
							</div>

							<div class="mb-4">
								<label class="block text-sm font-medium text-gray-700 mb-1">Icon</label>
								<div class="flex items-center gap-3 mb-3">
									<button
										class="bg-gray-100 text-gray-700 border border-gray-300 rounded-md px-4 py-2 text-sm cursor-pointer flex items-center gap-2 hover:bg-gray-200"
										@click="uploadIcon"
									>
										<i class="fa fa-upload"></i>
										Choose Icon
									</button>
									<span class="text-sm text-gray-500">{{ iconFileName || 'No file selected' }}</span>
								</div>
								<div v-if="category.icon" class="flex items-center gap-4 p-4 bg-gray-50 rounded-md">
									<img
										:src="category.icon"
										class="max-w-[100px] max-h-[100px] rounded object-contain"
										alt="Icon preview"
									>
									<button
										class="bg-red-50 text-red-500 border-none rounded px-3 py-1.5 text-sm cursor-pointer flex items-center gap-1 hover:bg-red-100"
										@click="removeIcon"
									>
										<i class="fa fa-times"></i> Remove
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Themes Tab -->
				<div v-show="activeTab === 'themes'" class="mb-6">
					<div class="bg-white rounded-lg shadow-sm overflow-hidden">
						<div class="flex justify-between items-center px-6 py-4 border-b border-gray-200">
							<h2 class="text-lg font-semibold text-gray-900 m-0">Linked Themes</h2>
							<span class="text-sm text-gray-500">{{ linkedThemes.length }} themes</span>
						</div>

						<div v-if="isLoadingThemes" class="py-12 px-6 text-center text-gray-500">
							Loading themes...
						</div>

						<div v-else-if="linkedThemes.length === 0" class="py-12 px-6 text-center text-gray-500">
							No themes linked to this category
						</div>

						<div v-else class="p-4">
							<div
								v-for="(theme, index) in linkedThemes"
								:key="theme.id"
								class="flex items-center gap-4 px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50"
							>
								<div class="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium text-gray-500">{{ index + 1 }}</div>
								<img
									v-if="theme.image"
									:src="theme.image"
									class="w-[60px] h-[60px] rounded object-cover"
									alt="Theme"
								>
								<div v-else class="w-[60px] h-[60px] bg-gray-100 rounded flex items-center justify-center text-[10px] text-gray-400">No image</div>
								<div class="flex-1">
									<div class="text-sm font-medium text-gray-900">{{ theme.name }}</div>
									<div class="text-xs text-gray-500">ID: {{ theme.id }}</div>
								</div>
								<button
									class="bg-blue-50 text-blue-500 border-none rounded px-2.5 py-1.5 cursor-pointer text-sm hover:bg-blue-100"
									title="View Theme"
									@click="viewTheme(theme.id)"
								>
									<i class="fa fa-external-link"></i>
								</button>
							</div>
						</div>
					</div>
				</div>

				<!-- Mobile Save Button -->
				<div class="hidden py-4">
					<button
						class="w-full bg-orange-7 text-white border-none rounded-md px-5 py-2.5 text-sm font-medium cursor-pointer hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
						:disabled="isSaving"
						@click="saveCategory"
					>
						{{ isSaving ? 'Saving...' : 'Save Changes' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ThemeCategoryModel, ThemeModel, AffiliateModel } from '@sosocio/creator/interfaces';
import { PickerResponse } from 'filestack-js';
import Swal from 'sweetalert2';
import ThemeCategoryService from 'services/ThemeCategoryService';
import AffiliateService from 'services/AffiliateService';

export default defineComponent({
	name: 'EditThemeCategoryPage',
	setup() {
		const route = useRoute();
		const router = useRouter();
		const instance = getCurrentInstance();
		const filestackClient = instance?.appContext.config.globalProperties.$client;

		const categoryId = route.params.id as string;

		const category = ref<Partial<ThemeCategoryModel>>({});
		const linkedThemes = ref<ThemeModel[]>([]);
		const trackers = ref<AffiliateModel[]>([]);
		const isLoading = ref(true);
		const isSaving = ref(false);
		const isLoadingThemes = ref(false);
		const activeTab = ref('properties');
		const iconFileName = ref('');

		const loadCategory = async () => {
			isLoading.value = true;
			try {
				const data = await ThemeCategoryService.get(categoryId);
				category.value = data;
			} catch (error: any) {
				console.error('Failed to load category:', error);
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load category',
					icon: 'error',
				});
			} finally {
				isLoading.value = false;
			}
		};

		const loadLinkedThemes = async () => {
			isLoadingThemes.value = true;
			try {
				const themes = await ThemeCategoryService.getAllThemes(categoryId);
				linkedThemes.value = themes;
			} catch (error: any) {
				console.error('Failed to load linked themes:', error);
			} finally {
				isLoadingThemes.value = false;
			}
		};

		const loadTrackers = async () => {
			try {
				const params = new URLSearchParams({ limit: '0' });
				const data = await AffiliateService.getAll(params);
				trackers.value = data;
			} catch (error: any) {
				console.error('Failed to load trackers:', error);
			}
		};

		const saveCategory = async () => {
			isSaving.value = true;
			try {
				await ThemeCategoryService.update(categoryId, category.value);
				Swal.fire({
					toast: true,
					position: 'top-end',
					icon: 'success',
					title: 'Category saved successfully',
					showConfirmButton: false,
					timer: 3000,
				});
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to save category',
					icon: 'error',
				});
			} finally {
				isSaving.value = false;
			}
		};

		const uploadIcon = () => {
			if (!filestackClient) {
				console.error('Filestack client not available');
				Swal.fire({
					title: 'Error',
					text: 'File upload service is not available. Please try again later.',
					icon: 'error',
				});
				return;
			}

			const options = {
				fromSources: ['local_file_system'],
				accept: ['image/*'],
				onUploadDone: (response: PickerResponse) => {
					if (response.filesUploaded.length > 0) {
						category.value.icon = response.filesUploaded[0].url;
						iconFileName.value = response.filesUploaded[0].filename;
					}
				},
			};
			filestackClient.picker(options).open();
		};

		const removeIcon = () => {
			category.value.icon = '';
			iconFileName.value = '';
		};

		const viewTheme = (themeId: number) => {
			router.push(`/theme/${themeId}`);
		};

		onMounted(() => {
			loadCategory();
			loadLinkedThemes();
			loadTrackers();
		});

		return {
			category,
			linkedThemes,
			trackers,
			isLoading,
			isSaving,
			isLoadingThemes,
			activeTab,
			iconFileName,
			saveCategory,
			uploadIcon,
			removeIcon,
			viewTheme,
		};
	},
});
</script>
